import { AGENTS, AGENT_LABEL } from '@tabel/shared/connector/versions';
import type { ConnectorView } from '@tabel/shared/schemas/agent-client';
import { Laptop, Unplug } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ErrorCodes, errorCode, errorSentence, errorText, useToast } from '../../components/ErrorToast';
import { AlertDialog, Badge, Button, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui';
import { disableConnector, isGone } from '../../lib/agent-api';
import { ApiError, reportClientError } from '../../lib/errors';
import { ruDateTime } from '../../lib/format';
import { CommandBox } from './AgentsCard';
import { ErrorCode, shouldReport } from './parts';
import { LABEL, OS_LABEL, PC_LABEL, PC_T, PC_TITLE, pcSay } from './texts';
import { sinceText } from './time';

/*
 * Карточка «Компьютеры» страницы «Мои ИИ-клиенты» (006, T042; US1-4, US1-5; FR-004, FR-006; contracts/ui.md §1,
 * мокап Clients.dc.html). Список — из `GET /api/agent/connectors` (запрос и обновление по WS ведёт страница).
 * - Строка: имя, система, версия коннектора, установленные агенты; бейдж «в сети» (`ok`), «не в сети с 14:05»
 *   (`warn`; не сегодня — с датой), «отключён» (нейтральный). Время — по времени объекта.
 * - «Показать агентов» выбирает компьютер для карточки «Агенты на …»: выбранная строка — `color-accent-soft`,
 *   у кнопки `aria-pressed`. «Отключить» — красная текстовая кнопка (дизайн-система, Button).
 * - Отключение необратимо — окно подтверждения: вопрос с именем компьютера, последствия числами (разговоры этого
 *   компьютера перестанут работать, переписка останется), «Отмена» в фокусе. Итог — список заново, сообщение и фокус на
 *   заголовке карточки. `404` — компьютера уже нет: список заново и сообщение.
 * - Состояния: загрузка — скелет строк, пусто — «Компьютеров нет», ошибка — причина, код ошибки и «Повторить».
 * - Один интерфейс на 375/768/1280 px: строка переносится — бейдж и кнопки под именем; кнопки на телефоне — 40 px.
 */

const SCREEN = 'MyAgents';

export interface ComputersCardProps {
  /** Список компьютеров; `undefined` — ещё грузится или не загрузился. */
  list: readonly ConnectorView[] | undefined;
  error: unknown;
  busy: boolean;
  onRetry: () => void;
  selectedId: number | null;
  onSelect: (id: number) => void;
  tz: string;
  /** Список заново: после отключения и после `404`. */
  reload: () => Promise<unknown>;
}

export function ComputersCard({
  list,
  error,
  busy,
  onRetry,
  selectedId,
  onSelect,
  tz,
  reload,
}: ComputersCardProps) {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [focusTick, setFocusTick] = useState(0);
  const [target, setTarget] = useState<ConnectorView | null>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [offShown, setOffShown] = useState(false);
  const { showInfo } = useToast();

  useEffect(() => {
    if (focusTick > 0) headingRef.current?.focus();
  }, [focusTick]);

  const open = (pc: ConnectorView) => {
    setFailure(null);
    setTarget(pc);
  };

  /** Отключение закончено: окно закрыто, сообщение, фокус — на заголовке (эффект идёт после эффекта окна). */
  const finish = (text: string) => {
    setTarget(null);
    showInfo(text);
    setFocusTick((t) => t + 1);
  };

  const confirm = async (pc: ConnectorView) => {
    setPending(true);
    setFailure(null);
    try {
      await disableConnector(pc.id);
      await reload();
      finish(pcSay.disabled(pc.name));
    } catch (e) {
      if (isGone(e)) {
        await reload();
        finish(pcSay.alreadyDisabled(pc.name));
      } else {
        setFailure(e);
        if (shouldReport(e)) reportClientError(SCREEN, 'disable-connector', e);
      }
    } finally {
      setPending(false);
    }
  };

  let body: ReactNode;
  if (list === undefined && error) {
    body = (
      <div className="border-t border-line">
        <EmptyState
          icon={Laptop}
          title={PC_TITLE.loadError}
          description={errorSentence(error)}
          action={
            <div className="flex flex-col items-center gap-3">
              <ErrorCode error={error} />
              <Button variant="primary" disabled={busy} onClick={onRetry}>
                {PC_LABEL.retry}
              </Button>
            </div>
          }
        />
      </div>
    );
  } else if (list === undefined) {
    body = <Loading />;
  } else if (list.length === 0) {
    body = (
      <div className="border-t border-line">
        <EmptyState icon={Laptop} title={PC_TITLE.empty} description={PC_T.empty} />
      </div>
    );
  } else {
    // Отключённые компьютеры — свёрнуты под списком (приёмка 18.10.2026: после повторной установки в списке было две
    // записи с одним именем). Записи остаются для журнала; раскрываются по нажатию.
    const row = (pc: ConnectorView) => (
      <ComputerRow
        key={pc.id}
        pc={pc}
        tz={tz}
        selected={pc.id === selectedId}
        onSelect={() => onSelect(pc.id)}
        onDisable={() => open(pc)}
      />
    );
    const active = list.filter((pc) => pc.phase !== 'disabled');
    const off = list.filter((pc) => pc.phase === 'disabled');
    body = (
      <>
        {active.length > 0 ? (
          <ul aria-label={PC_TITLE.computers} className="divide-y divide-line border-t border-line">
            {active.map(row)}
          </ul>
        ) : (
          <div className="border-t border-line">
            <EmptyState icon={Laptop} title={PC_TITLE.empty} description={PC_T.empty} />
          </div>
        )}
        {off.length > 0 && (
          <div className="border-t border-line px-6 py-2 max-sm:px-4">
            <Button variant="ghost" aria-expanded={offShown} onClick={() => setOffShown((v) => !v)}>
              {pcSay.disabledComputers(off.length)}
            </Button>
          </div>
        )}
        {offShown && off.length > 0 && (
          <ul aria-label={PC_TITLE.disabledComputers} className="divide-y divide-line border-t border-line">
            {off.map(row)}
          </ul>
        )}
      </>
    );
  }

  return (
    <section aria-labelledby={headingId}>
      <Card>
        <CardHeader
          id={headingId}
          headingRef={headingRef}
          title={PC_TITLE.computers}
          description={PC_T.computers}
        />
        {body}
      </Card>
      <AlertDialog
        open={target !== null}
        // Пока идёт отключение, окно не закрывается: итог покажет сообщение.
        onClose={() => {
          if (!pending) setTarget(null);
        }}
        title={target ? pcSay.disableTitle(target.name) : ''}
        icon={Unplug}
        tone="bad"
        description={
          <>
            {target ? pcSay.disableText(target.threads) : ''}
            {failure !== null && <DisableError error={failure} />}
          </>
        }
      >
        <Button data-autofocus disabled={pending} onClick={() => setTarget(null)}>
          {PC_LABEL.cancel}
        </Button>
        <Button variant="danger" disabled={pending} onClick={() => target && void confirm(target)}>
          {pending ? PC_LABEL.disabling : PC_LABEL.disableConfirm}
        </Button>
      </AlertDialog>
    </section>
  );
}

function ComputerRow({
  pc,
  tz,
  selected,
  onSelect,
  onDisable,
}: {
  pc: ConnectorView;
  tz: string;
  selected: boolean;
  onSelect: () => void;
  onDisable: () => void;
}) {
  const nameId = useId();
  const os = OS_LABEL[pc.os as keyof typeof OS_LABEL] ?? pc.os;
  const disabled = pc.phase === 'disabled';
  const [removeShown, setRemoveShown] = useState(false);
  // Состояния агентов ещё нет (компьютер только что подключился) — про агентов ничего не пишем.
  const installed =
    pc.agents.length === 0
      ? null
      : AGENTS.filter((a) => pc.agents.some((h) => h.agent === a && h.state !== 'not_installed')).map(
          (a) => AGENT_LABEL[a],
        );
  const line = disabled
    ? pcSay.pcDisabledLine(os, ruDateTime(pc.disabledAt, tz))
    : pcSay.pcLine(os, pc.version, installed);
  const badge = disabled ? (
    <Badge dot>{PC_LABEL.disabled}</Badge>
  ) : pc.online ? (
    <Badge tone="ok" dot>
      {PC_LABEL.online}
    </Badge>
  ) : (
    <Badge tone="warn" dot>
      {pcSay.offlineSince(sinceText(pc.lastSeenAt, tz))}
    </Badge>
  );
  return (
    <li
      aria-labelledby={nameId}
      className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-6 py-3 max-sm:px-4 ${selected && !disabled ? 'bg-accent-soft' : ''}`}
    >
      <div className="flex min-w-0 flex-[1_1_260px] items-center gap-2.5">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-subtle text-ink-2">
          <Laptop size={18} strokeWidth={1.75} aria-hidden />
        </span>
        <div className="min-w-0">
          <h3 id={nameId} className="text-[14px] font-semibold break-all text-ink">
            {pc.name}
          </h3>
          <p className="text-[12.5px] text-ink-2 tabular-nums">{line}</p>
        </div>
      </div>
      <span data-badge className="shrink-0">
        {badge}
      </span>
      {disabled && (
        <div className="flex shrink-0 max-sm:w-full">
          <Button variant="ghost" aria-expanded={removeShown} onClick={() => setRemoveShown((v) => !v)}>
            {PC_LABEL.howToRemove}
          </Button>
        </div>
      )}
      {disabled && removeShown && (
        <div className="w-full space-y-2 pl-[46px] text-[13px] text-ink-2 max-sm:pl-0">
          <p>{PC_T.removeManual}</p>
          <CommandBox command={removeCommand()} agentName={pc.name} />
        </div>
      )}
      {!disabled && (
        <div className="flex shrink-0 flex-wrap gap-2 max-sm:w-full max-sm:[&>button]:flex-1">
          <Button
            variant="ghost"
            aria-pressed={selected}
            aria-label={pcSay.showAgentsOf(pc.name)}
            onClick={onSelect}
          >
            {PC_LABEL.showAgents}
          </Button>
          <Button variant="dangerText" aria-label={pcSay.disableName(pc.name)} onClick={onDisable}>
            {PC_LABEL.disable}
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Команда удаления коннектора на компьютере (FR-004). Папка коннектора — по имени сервера (`install.sh`: адрес без
 * схемы и порта).
 */
export function removeCommand(host: string = window.location.hostname): string {
  return `node "$HOME/.tabel-connector/${host}/connector.mjs" uninstall`;
}

function Loading() {
  return (
    <div aria-busy="true" aria-label={PC_LABEL.loading} className="divide-y divide-line border-t border-line">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center gap-3 px-6 py-3 max-sm:px-4">
          <Skeleton className="h-9 w-9 shrink-0" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
      ))}
    </div>
  );
}

/** Ошибка отключения в окне: окно остаётся, текст и код ошибки (requestId) для команды разработки. */
function DisableError({ error }: { error: unknown }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <span role="alert" className="mt-3 block rounded-control bg-bad-soft px-3 py-2 text-[13px] text-bad">
      <span className="block font-medium">{PC_T.disableFailed}.</span>
      <span className="block">{errorText(error)}</span>
      {(requestId || code) && (
        <span className="mt-0.5 block text-[12px]">
          {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
        </span>
      )}
    </span>
  );
}
